import { useEffect, useLayoutEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { LoaderCircle, ArrowRight, Sparkles } from "lucide-react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import authService from "@/services/auth.service";
import Alert from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/auth.context";
import { Link, useLocation, useNavigate } from "react-router-dom";
import qs from "qs";
import { useOrganization } from "@/context/organization.context";
import useDomain from "@/hooks/use-domain";
import { useTranslation } from "react-i18next";
import { parseAuthError } from "@/lib/auth-errors";

// Note: Validation schema needs to be inside component or use a factory function
// to access translations for validation messages
const createFormSchema = (t: (key: string) => string) =>
  yup.object().shape({
    email: yup
      .string()
      .email(t("auth:validation.emailInvalid"))
      .required(t("auth:validation.emailRequired"))
      .label("Email"),
    password: yup
      .string()
      .required(t("auth:validation.passwordRequired"))
      .label("Password"),
  });

export default function Login() {
  const { t } = useTranslation(["auth", "navbar"]);
  const location = useLocation();
  const { activeOrg } = useOrganization();
  const year = new Date().getFullYear();

  const { settings } = useSettings();

  const form = useForm({
    resolver: yupResolver(createFormSchema(t)),
    values: {
      email: "",
      password: "",
    },
  });

  const redirect = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  })?.redirect;

  const defaultEmail = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  })?.email;

  useEffect(() => {
    if (defaultEmail) {
      form.setValue("email", defaultEmail);
    }
  }, [defaultEmail]);

  const { setCurrentUser } = useAuth();

  const navigate = useNavigate();
  const [error, setError] = useState("");

  const { isAdmin, orgDomain } = useDomain(env_config[app_env].domain);

  const submit = async (values: any) => {
    setError("");
    try {
      setError("");
      const res = await authService.signIn({
        email: values.email,
        password: values.password,
      });
      setCurrentUser(res);
      navigate(redirect || (!orgDomain ? "/choose-org" : "/choose-module"));
    } catch (e) {
      const { message, fieldErrors } = parseAuthError(e, t);
      setError(message);
      Object.keys(fieldErrors)?.forEach((field: any) => {
        form.setError(field, {
          message: fieldErrors[field],
        });
      });
    }
  };

  const queryParams = new URLSearchParams(location.search);
  const queryString = queryParams.toString(); // This will give you the query string

  const [mode, setMode] = useState("password");

  useLayoutEffect(() => {
    if (settings?.enable_passcode_auth && orgDomain) {
      setMode("passcode");
    }
  }, [settings?.enable_passcode_auth, orgDomain]);

  return (
    <div className="min-h-screen flex">
      {/* Left Side - Branding */}
      <div className="hidden lg:flex lg:w-1/2 lg:fixed lg:inset-y-0 lg:left-0 bg-primaryDark dark:bg-primaryDark relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10 dark:opacity-[3%]">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#fff_1px,transparent_1px),linear-gradient(to_bottom,#fff_1px,transparent_1px)] bg-[size:4rem_4rem]" />
        </div>

        <div className="relative z-10 flex flex-col justify-between p-12 text-white">
          {/* Logo */}
          <Link
            to={"/"}
            // className="flex bg-card w-fit px-3 py-2 rounded-md items-center gap-2"
          >
            <img className="h-7" src="logo_white.png" alt="" />
          </Link>
          {/* Main Content */}
          <div className="space-y-8">
            <div className="inline-flex items-center gap-2 bg-card/20 px-4 py-2 rounded-md text-sm">
              <Sparkles className="w-4 h-4" />
              {t("navbar:hero.trustedBy")}
            </div>

            <h1 className="text-4xl xl:text-4xl font-semibold leading-tight">
              {t("navbar:hero.title")}
            </h1>

            <p className="text-base text-white/80 leading-relaxed max-w-md">
              {t("navbar:hero.subtitle")}
            </p>

            {/* Feature List */}
            <div className="space-y-4 text-sm">
              {[
                "realTimeAnalytics",
                "multiLocationSupport",
                "customerSupport",
              ].map((featureKey, i) => (
                <div key={i} className="flex items-center gap-3">
                  <div className="h-5 w-5 rounded-md bg-card/20 flex items-center justify-center">
                    <svg
                      className="w-3 h-3"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </div>
                  <span className="text-white/90">
                    {t(`auth:login.features.${featureKey}`)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Bottom */}
          <div className="flex items-center gap-4">
            <div className="flex -space-x-2">
              {["JD", "AK", "MR", "SL"].map((initials, i) => (
                <div
                  key={i}
                  className="w-9 h-9 rounded-full bg-card/20 backdrop-blur border-2 border-white/30 flex items-center justify-center text-xs font-medium"
                >
                  {initials}
                </div>
              ))}
            </div>
            <div className="text-sm text-white/80">
              {t("auth:login.rating")}
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex flex-col bg-background lg:ml-[50%]">
        {/* Header */}
        <header className="px-4 py-2 sm:p-6 flex justify-between items-center"></header>

        {/* Form Content */}
        <div className="flex-1 flex flex-col max-w-sm mx-auto w-full  items-center justify-center px-4 py-8">
          {mode === "passcode" && <PinLoginForm setMode={setMode} />}

          {mode === "password" && (
            <Card className="border w-full border-border rounded-md">
              <CardHeader className="space-y-1 pb-4">
                <div className="flex items-center justify-center">
                  <img className="h-8 mb-2" src="/logo_iconn.png" alt="" />
                </div>
                <CardTitle className="text-lg font-semibold text-center text-foreground">
                  {t("auth:login.formTitle")}
                </CardTitle>
                <CardDescription className="text-center text-muted-foreground">
                  {t("auth:login.formSubtitle")}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {error && <Alert variant="danger">{error}</Alert>}

                <form
                  onSubmit={form.handleSubmit(submit)}
                  className="space-y-3"
                >
                  <div className="space-y-1">
                    <Label htmlFor="email" className="text-muted-foreground">
                      {t("auth:login.email")}
                    </Label>
                    <Input
                      placeholder={t("auth:login.emailPlaceholder")}
                      value={form.watch("email")}
                      onChange={(e) => form.setValue("email", e.target.value)}
                      error={form.formState?.errors["email"]?.message}
                      name="email"
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <Label
                        htmlFor="password"
                        className="text-muted-foreground"
                      >
                        {t("auth:login.password")}
                      </Label>
                      <Link
                        className="text-sm text-primaryDark hover:text-primary font-medium"
                        to="/forgot-password"
                      >
                        {t("auth:login.forgotPassword")}
                      </Link>
                    </div>
                    <Input
                      placeholder={t("auth:login.passwordPlaceholder")}
                      value={form.watch("password")}
                      onChange={(e) =>
                        form.setValue("password", e.target.value)
                      }
                      type="password"
                      error={form.formState?.errors["password"]?.message}
                      name="password"
                      className="h-11"
                    />
                  </div>
                  <Button
                    disabled={form.formState.isSubmitting}
                    className="w-full bg-primary !mt-5 hover:bg-primaryDark text-white"
                    type="submit"
                  >
                    {form.formState.isSubmitting ? (
                      <LoaderCircle className="animate-spin" size={18} />
                    ) : (
                      <>
                        {t("auth:login.signInAccount")}
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </>
                    )}
                  </Button>
                </form>

                <div className="mt-4 text-center">
                  <p className="text-sm text-muted-foreground">
                    Want to get started?{" "}
                    <a
                      href="tel:+250788209629"
                      className="text-primaryDark hover:text-primaryDark hover:underline font-medium"
                    >
                      Contact us
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {activeOrg && (
            <div className="text-center w-full text-sm text-muted-foreground mt-4 bg-card rounded-md py-2 px-4 border border-border">
              {t("auth:login.signingInto")}{" "}
              <span className="font-medium text-foreground">
                {activeOrg?.name}
              </span>
            </div>
          )}
          {isAdmin && (
            <div className="text-center text-sm text-muted-foreground mt-4 bg-amber-50 rounded-md py-2 px-4 border border-amber-200">
              <span className="font-medium text-amber-700">
                {t("auth:login.adminPortal")}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="p-4 sm:px-6 sm:py-4 border-t border-border bg-card">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-2 text-sm text-muted-foreground">
            <p>{t("auth:login.footerRights", { year })}</p>
            <div className="flex gap-4">
              <Link to="#" className="hover:text-muted-foreground">
                {t("auth:login.terms")}
              </Link>
              <Link to="#" className="hover:text-muted-foreground">
                {t("auth:login.privacy")}
              </Link>
              <Link to="#" className="hover:text-muted-foreground">
                {t("auth:login.help")}
              </Link>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

import { Delete, Shield } from "lucide-react";
import { cn } from "@/lib/utils";
import useSettings from "@/hooks/useSettings";
import { app_env, env_config } from "@/env-config";

export function PinLoginForm({ setMode }) {
  const { t } = useTranslation("auth");
  const [pin, setPin] = useState("");
  const [showPin] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [attempts, setAttempts] = useState(0);

  const maxLength = 6;
  const digitKeyClass =
    "h-12 text-lg font-semibold border-border rounded-md transition-none hover:bg-background hover:border-border hover:text-foreground active:bg-background focus-visible:ring-0 focus-visible:ring-offset-0 disabled:opacity-50";

  const handleNumberClick = (number: number) => {
    if (pin.length < maxLength) {
      setPin((prev) => prev + number.toString());
      setError("");
    }
  };

  const handleDelete = () => {
    setPin((prev) => prev.slice(0, -1));
    setError("");
  };

  const handleClear = () => {
    setPin("");
    setError("");
  };

  const { orgDomain } = useDomain("one-suite.cloud");

  const redirect = qs.parse(location.search, {
    ignoreQueryPrefix: true,
  })?.redirect;

  const { setCurrentUser } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async () => {
    if (pin.length === 0) return;

    setIsLoading(true);
    setError("");

    try {
      const res = await authService.loginWithPasscode({
        passcode: pin,
        domain: orgDomain,
      });
      setPin("");
      setCurrentUser(res);

      // A locked subscription only lets billing managers through this door
      // (see `assertLoginAccess`), so anyone holding this response with
      // `locked` can pay — send them where they can. Everyone else never gets
      // here: the server refuses them with the vague message above.
      if ((res as { billing?: { state?: string } })?.billing?.state === "locked") {
        navigate("/billing-required?canPay=1");
        return;
      }

      navigate(redirect || (orgDomain ? "/choose-module" : "/choose-org"));
    } catch (error) {
      const { message } = parseAuthError(error, t);
      setError(message);
      setAttempts((prev) => prev + 1);
      setPin("");
    }

    setIsLoading(false);
  };

  // Auto-submit when PIN reaches max length
  useEffect(() => {
    if (pin.length === maxLength) {
      handleSubmit();
    }
  }, [pin]);

  // Handle keyboard input
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (isLoading) return;

      if (e.key >= "0" && e.key <= "9") {
        handleNumberClick(parseInt(e.key));
      } else if (e.key === "Backspace") {
        handleDelete();
      } else if (e.key === "Enter" && pin.length > 0) {
        handleSubmit();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [pin, isLoading]);

  return (
    <Card className="border border-border rounded-md w-full max-w-[420px] mx-auto">
      <CardHeader className="text-center space-y-3 pb-2">
        <div className="mx-auto w-10 h-10 bg-primary rounded-md flex items-center justify-center">
          <Shield className="w-5 h-5 text-white" />
        </div>
        <div>
          <CardTitle className="text-lg font-semibold text-foreground">
            {t("passcode.title")}
          </CardTitle>
          <CardDescription className="text-muted-foreground mt-1">
            {t("passcode.subtitle", { length: maxLength })}
          </CardDescription>
        </div>
      </CardHeader>

      <CardContent className="space-y-6 pt-2">
        {/* PIN Display */}
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-2">
            {Array.from({ length: maxLength }).map((_, index) => (
              <div
                key={index}
                className={cn(
                  "w-10 h-10 rounded-md border-2 flex items-center justify-center text-xl font-bold transition-all duration-150",
                  index < pin.length
                    ? "border-primary bg-primary/5 text-primary"
                    : index === pin.length
                      ? "border-primary/50 bg-card"
                      : "border-border bg-backgroud",
                )}
              >
                {index < pin.length && (
                  <span className={showPin ? "" : "mt-1-"}>
                    {showPin ? pin[index] : "●"}
                  </span>
                )}
              </div>
            ))}
          </div>

          {error && (
            <div className="flex items-center justify-center gap-2 text-red-600 text-sm font-medium bg-red-50 py-2 px-4 rounded-md border border-red-200">
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              {error}
            </div>
          )}

          {attempts >= 3 && (
            <p className="text-amber-600 text-xs text-center bg-amber-50 py-2 px-3 rounded-md border border-amber-200">
              {t("passcode.tooManyAttempts")}
            </p>
          )}
        </div>

        {/* Number Pad */}
        {/* A digit key never changes colour on hover, press or focus: a key
            that lights up tells whoever is standing behind the cashier which
            digits the PIN is made of. */}
        <div className="space-y-2">
          <div className="grid grid-cols-3 gap-2">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((number) => (
              <Button
                key={number}
                variant="outline"
                onClick={() => handleNumberClick(number)}
                disabled={isLoading || pin.length >= maxLength}
                className={digitKeyClass}
              >
                {number}
              </Button>
            ))}
          </div>

          <div className="grid grid-cols-3 gap-2">
            <Button
              variant="outline"
              onClick={handleClear}
              disabled={isLoading || pin.length === 0}
              className="h-12 text-sm font-medium border-border rounded-md hover:bg-accent/50 transition-colors"
            >
              {t("passcode.clear")}
            </Button>
            <Button
              variant="outline"
              onClick={() => handleNumberClick(0)}
              disabled={isLoading || pin.length >= maxLength}
              className={digitKeyClass}
            >
              0
            </Button>
            <Button
              variant="outline"
              onClick={handleDelete}
              disabled={isLoading || pin.length === 0}
              className="h-12 border-border rounded-md hover:bg-accent/50 transition-colors"
            >
              <Delete className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Submit Button */}
        <div className="space-y-3">
          <Button
            onClick={handleSubmit}
            disabled={isLoading || pin.length === 0}
            className="w-full h-11 text-base bg-primary hover:bg-primaryDark text-white font-medium rounded-md"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <LoaderCircle className="w-5 h-5 animate-spin" />
                {t("passcode.verifying")}
              </div>
            ) : (
              <>
                {t("passcode.signIn")}
                <ArrowRight className="w-4 h-4 ml-2" />
              </>
            )}
          </Button>

          <p className="text-sm text-center text-muted-foreground">
            {t("passcode.preferEmail")}{" "}
            <button
              onClick={() => setMode("password")}
              className="text-primary hover:text-primaryDark font-medium"
            >
              {t("passcode.usePasswordInstead")}
            </button>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
